"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Layers, Sparkles, Compass, Hammer, CircleCheck as CheckCircle2, ArrowRight, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const additionFeatures = [{
  title: "Second-story pop-ups",
  description: "Expand upward to double your living area without cutting into mature Brookhaven trees or yard space.",
  criteria: ["Reinforced first-floor load paths", "Architectural pitch and eave matching", "Sound-damped subfloor assemblies"],
  icon: Layers
}, {
  title: "Primary suite extensions",
  description: "A quiet main-level retreat tailored with curbless showers, custom millwork dressing rooms, and porch access.",
  criteria: ["Zero-threshold floor transitions", "Dedicated high-efficiency HVAC zone", "Acoustic insulation batts throughout"],
  icon: Sparkles
}, {
  title: "Mudrooms & sculleries",
  description: "Streamlined drop zones and hidden prep kitchens connecting the garage, pantry, and daily living areas.",
  criteria: ["Waterproof tile with in-floor heat", "Integrated pet wash and drying stations", "Custom locker and cabinet storage"],
  icon: Compass
}, {
  title: "Foundation & structural tie-ins",
  description: "Deep footings and precise masonry dovetailing that protect structural stability for decades to come.",
  criteria: ["Soil bearing density testing", "Engineered rebar dowel pinning", "City of Brookhaven permit handling"],
  icon: Hammer
}];
export default function Additions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="home-additions" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 rounded-full border-primary/40 bg-primary/10 text-primary-foreground font-medium px-3.5 py-1 text-xs tracking-wider uppercase">
            Residential Expansions
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Thoughtful additions built for Brookhaven living
          </h2>

          <div className="w-20 h-0.5 bg-primary/50 my-6" />

          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans">
            Every expansion blends cleanly into your home&apos;s original roofline, brickwork, and neighborhood scale. From municipal zoning setbacks to historic preservation parameters, we will follow up to discuss your project, handling architectural engineering and city permitting from day one.
          </p>
        </motion.div>

        {/* Asymmetric Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column: 7 Cols Feature Cards */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {additionFeatures.map((feature, i) => {
            const IconComponent = feature.icon;
            return <motion.div key={feature.title} variants={itemVariants} data-index={i} whileHover={shouldReduceMotion ? {} : {
              y: -4
            }} className="h-full">
                  <Card className="h-full border border-border/70 bg-card p-6 shadow-sm hover:border-primary/50 hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-250 flex flex-col justify-between">
                    <div>
                      <div className="size-11 rounded-lg bg-accent/60 flex items-center justify-center text-primary mb-5 ring-1 ring-border">
                        <IconComponent className="size-5" />
                      </div>

                      <CardHeader className="p-0 gap-2 mb-3">
                        <CardTitle className="font-serif text-xl font-medium tracking-tight text-foreground">
                          {feature.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="p-0 text-sm text-foreground/75 leading-relaxed font-sans mb-5">
                        {feature.description}
                      </CardContent>
                    </div>

                    <div className="pt-4 border-t border-border/50">
                      <ul className="space-y-2">
                        {feature.criteria.map((item, idx) => <li key={idx} className="flex items-start text-xs text-foreground/70 font-sans">
                            <CheckCircle2 className="size-3.5 text-primary mt-0.5 mr-2 shrink-0" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Right Column: 5 Cols Architectural Showcase Plinth */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-lg border border-border bg-card">
              {/* Architectural Exterior Photo */}
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_home_addition_exterior.webp-wvc-sizes" alt="Seamless two-story home addition with matched brickwork and architectural rooflines in Brookhaven, Georgia" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-secondary/15 mix-blend-multiply pointer-events-none" />
              </div>

              {/* Atelier Frosted Glass Overlay Card */}
              <div className="p-6 sm:p-7 bg-card/90 backdrop-blur-md border-t border-border/80">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="size-4 text-primary" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Local Compliance & Craft
                  </span>
                </div>

                <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                  Historic preservation & zoning mastery
                </h3>

                <p className="text-sm text-foreground/80 leading-relaxed font-sans mb-5">
                  Brookhaven tree ordinances, stormwater plans, and structural setbacks require meticulous pre-construction coordination. We manage complete engineering documentation to secure approvals without delays.
                </p>

                <div className="flex items-center pt-4 border-t border-border/60">
                  <div className="text-xs text-foreground/75 font-medium">
                    Permit Management Included
                  </div>
                </div>

                <div className="mt-6 pt-2">
                  <Button asChild size="default" className="w-full">
                    <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center justify-center gap-2 font-medium">
                      <span>Consult with our principal builder</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Context Strip */}
        <div className="mt-16 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-foreground/70">
          <p className="text-center sm:text-left">
            Serving Brookhaven neighborhoods including Ashford Park, Historic Brookhaven, Brittany, and Drew Valley.
          </p>
          <Link to="/OurProjectsPage" className="text-primary hover:underline underline-offset-4 inline-flex items-center gap-1 font-medium">
            <span>Browse completed home additions</span>
            <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </section>;
}